import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import HeroReviewRow from "../HeroReviewRow";
import HeroSocialProof from "../HeroSocialProof";
import HeroTrustpilot from "../HeroTrustpilot";
import { FLOW_MEASURE } from "./landingRun";
import Breadcrumb from "@/components/marketing/Breadcrumb";
import {
  BODY,
  BODY_SMALL,
  HEADING_2,
} from "@/components/marketing/marketingType";
import {
  SECTION_PANEL,
  SECTION_WASH,
} from "@/components/marketing/sectionPanel";
import {
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Check, LandingBlock } from "@/content/landing/types";
import {
  CheckLine,
  LandingButton,
  LandingImage,
  LandingSection,
  LandingVisual,
} from "../LandingPrimitives";
import MatrixTable from "./MatrixTable";
import { heroLayout, type HeroLayout } from "@/content/landing/heroLayout";
import { usePageIdentity } from "../LandingPageContext";
import { Lines } from "@/components/marketing/Lines";

/**
 * What the hero draws: the block's words and pictures, and the layout its page
 * publishes. The two come from different places on purpose, and
 * `content/landing/heroLayout.ts` says why: an editor writes the words, while
 * "pad the box to 32" is a fact about the published design that belongs in
 * code. `HeroBlock` merges them once, so everything below reads one object.
 */
type Hero = Extract<LandingBlock, { kind: "hero" }> & HeroLayout;

/**
 * `main_header_img_height`: the box every published hero draws its art in. It
 * is a square, stated there as `padding-top: 100%`, so it shrinks with the
 * column rather than holding the desktop height at every width.
 */
const HERO_VISUAL_WIDTH = 600;
/** `use-case-main-header_img`: the one hero art that is capped, not sized. */
const HERO_ART_CAP = 579;
/** `main_header_box`: its own padding, not the section's. */
const HERO_BOX_PAD_COMPACT = 32;
/** The banner spacer some published pages sit their hero section inside. */
const HERO_BANNER_SPACER = 24;
/** The measure the copy is set to, and the narrower one some pages use. */
const HERO_COPY_WIDTH = 544;
const HERO_COPY_NARROW = 464;

/**
 * `main_header_button_holder`: the width a lone hero control is drawn at, and
 * the two the published pages state instead of it. `is-dc` also carries a min,
 * so its control keeps its width where the pair beside it gives way.
 */
const CTA_LONE = "283.031px";
const CTA_FIXED = "200px";
const CTA_WIDE = "220px";

/** The published hero section closes on its box: the rhythm below is the next section's. */
const NO_SECTION_TAIL = {
  pb: 0,
  [NOT_WIDE]: { pb: 0 },
  [MID]: { pb: 0 },
  [TINY]: { pb: 0 },
} as const;

/**
 * The line under the hero buttons: `text-btn_holder` where the hero is centred,
 * `main_header_text_btn_holder` where it is left. An item with no icon takes a
 * drawn dot instead, an element of its own so the strip stays two phrases
 * rather than one joined string.
 */
function ReassuranceStrip({
  items,
  align,
  shrink,
}: {
  items: ReadonlyArray<Check>;
  align?: "center";
  shrink?: true;
}) {
  const centred = align === "center";
  const phrase = {
    display: "flex",
    alignItems: "center",
    ...(centred
      ? {
          gap: "6.4px",
          fontSize: 18,
          lineHeight: "27px",
          [TINY]: { fontSize: 16, lineHeight: "24px" },
        }
      : { gap: "6.08px" }),
  };
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        ...(centred
          ? {
              justifyContent: "center",
              columnGap: "19.2px",
              [TINY]: { rowGap: "4.8px" },
            }
          : {
              // Column gap only: wrapped, the rows meet rather than opening another 6.
              columnGap: "6.08px",
              fontSize: 14,
              lineHeight: "21px",
              color: "rgba(79, 92, 101, 0.8)",
              [TINY]: {
                justifyContent: "center",
                ...(shrink ? { fontSize: 13, lineHeight: "19.5px" } : {}),
              },
            }),
      }}
    >
      {items.map((item, index) => (
        <Box key={index} sx={phrase}>
          {index > 0 && !item.icon ? (
            <Box
              aria-hidden
              sx={{
                width: "4px",
                height: "4px",
                borderRadius: "50%",
                backgroundColor: marketingPalette.inkSubtle,
              }}
            />
          ) : null}
          {item.icon ? (
            <LandingImage
              src={item.icon}
              eager
              sx={{
                height: "21px",
                width: "21px",
                [TINY]: { height: "16px", width: "16px" },
              }}
            />
          ) : null}
          <Typography sx={{ fontSize: "inherit", lineHeight: "inherit" }}>
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

/**
 * `integration_component`: the tool logos the integrations hero closes on. The
 * panel is stated twice inside a track twice the frame; the duplicate is what
 * makes the loop seamless.
 */
function LogoMarquee({ logos }: { logos: ReadonlyArray<string> }) {
  return (
    <Box sx={{ display: "flex", overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          flexShrink: 0,
          animation: "landing-logo-track 40s linear infinite reverse",
          "@keyframes landing-logo-track": {
            from: { transform: "translateX(0%)" },
            to: { transform: "translateX(-50%)" },
          },
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      >
        {[0, 1].map((panel) => (
          <Box
            key={panel}
            aria-hidden={panel === 1}
            sx={{
              display: "flex",
              justifyContent: "space-around",
              gap: "8px",
              pr: "8px",
              flexShrink: 0,
            }}
          >
            {logos.map((logo, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  width: "144px",
                  height: "104px",
                  borderRadius: "16px",
                  backgroundColor: marketingPalette.surface,
                  // `.integration_item` on a phone: 96 by 64.
                  [TINY]: { width: "96px", height: "64px" },
                }}
              >
                <LandingImage
                  src={logo}
                  eager
                  sx={{
                    width: "79px",
                    height: "57px",
                    objectFit: "contain",
                    [TINY]: { maxWidth: "80%", maxHeight: "80%" },
                  }}
                />
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/** `.hero-visual_box.is-grey`: the ground the comparison heroes are set on. */
const CENTRED_PANEL = {
  p: "32px",
  borderRadius: "32px",
  backgroundColor: marketingPalette.surface,
  [NOT_WIDE]: { p: "16px" },
};

/** The paragraphs under a hero title: the first at body size, the rest a step smaller. */
function HeroParagraphs({ paragraphs }: { paragraphs: Hero["paragraphs"] }) {
  return paragraphs?.map((paragraph, index) => (
    <Typography key={index} sx={index === 0 ? BODY : BODY_SMALL}>
      {paragraph}
    </Typography>
  ));
}

/** The words: the title on its measure and the paragraphs under it. */
function CentredWords({ block }: { block: Hero }) {
  return (
    // `.hero-title_list_short`: sixty of the page's 16px characters wide (610
    // at every step); `.hero_integrate_title` is 589.
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: block.wash ? "12.8px" : "16px",
        fontSize: 16,
        maxWidth: block.wash ? "60ch" : "589px",
      }}
    >
      <Typography
        variant="h1"
        component="h1"
        sx={{ color: marketingPalette.ink }}
      >
        <Lines text={block.title} />
      </Typography>
      <HeroParagraphs paragraphs={block.paragraphs} />
    </Box>
  );
}

/** The controls, and the reassurance line under them. */
function CentredControls({ block }: { block: Hero }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: block.wash ? "12.8px" : "8px",
        width: 1,
      }}
    >
      {block.ctas?.length ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "16px",
            flexWrap: "wrap",
            width: 1,
            "& > a, & > button": { width: "auto" },
            ...(block.heroControl
              ? { "& > :first-of-type": { width: "224px", flex: "none" } }
              : {}),
            [MID]: {
              flexDirection: "column",
              "& > a, & > button, & > :first-of-type": { width: "100%" },
            },
            [TINY]: { gap: "12px" },
          }}
        >
          {block.ctas.map((cta, index) => (
            <LandingButton
              key={index}
              cta={cta}
              variant={secondVariant(index, block)}
            />
          ))}
        </Box>
      ) : null}
      {block.reassurances?.length ? (
        <ReassuranceStrip items={block.reassurances} align="center" />
      ) : null}
    </Box>
  );
}

/** The frame: the plain column, or `.hero-visual_box` where the page names a wash. */
function centredFrameSx(block: Hero) {
  return [
    // `.hero_integrate_wrap`: 80 between the words and the logos until the
    // phone step, 48 on it.
    {
      display: "flex",
      flexDirection: "column",
      gap: "80px",
      [TINY]: { gap: "48px" },
    },
    // `.hero-visual_box`: its parts 24 apart at every step, and on a
    // phone the box stands 16 under the section's own 48.
    block.wash
      ? [
          CENTRED_PANEL,
          {
            gap: "24px",
            pb: "32px",
            [NOT_WIDE]: { gap: "24px", pb: "16px" },
            [TINY]: { mt: "16px" },
          },
        ]
      : {},
  ].flat();
}

function CentredHero({ block }: { block: Hero }) {
  return (
    <LandingSection padding="top">
      <Box sx={centredFrameSx(block)}>
        {/* `.header-hp_top` on the comparison box sets the words, the controls
            and the reassurance line 12.8 apart; `.hero_integrate_title_wrap`
            runs 32 over a title on 16 and a control stack on 8. */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: block.wash ? "12.8px" : "32px",
          }}
        >
          <CentredWords block={block} />
          <CentredControls block={block} />
        </Box>
        {block.logos?.length ? <LogoMarquee logos={block.logos} /> : null}
        {/* `compare-wrapper-center`: the comparison hero closes on its own table. */}
        {block.table ? (
          <Box sx={{ width: 1, maxWidth: "800px", mx: "auto" }}>
            <MatrixTable
              columns={block.table.columns}
              rows={block.table.rows}
              verdict
            />
          </Box>
        ) : null}
      </Box>
    </LandingSection>
  );
}

/**
 * `header-compare_title-list`: the hero that is a section heading and nothing
 * else: no box, no controls, set centred on a measure of its own.
 */
function HeadingHero({ block }: { block: Hero }) {
  return (
    <LandingSection
      padding="hero"
      sx={[
        NO_SECTION_TAIL,
        // This one hero opens 16 lower than a hero box does, at both steps:
        // stated twice, since the section's own are media rules.
        { [NOT_WIDE]: { pt: "26px" }, [TINY]: { pt: "26px" } },
      ]}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          // The two lines are centred as boxes and set to their own measure;
          // the published page centres neither line's own text until the
          // narrowest step, where its mobile heading centres both.
          alignItems: "center",
          gap: "3.2px",
          width: 1,
          maxWidth: "609.714px",
          mx: "auto",
          [TINY]: { textAlign: "center" },
        }}
      >
        {/* The published page heads this one `h2`; the page's own `h1`, since
            no other heading on it outranks the hero's. */}
        <Typography variant="h2" component="h1" sx={HEADING_2}>
          <Lines text={block.title} />
        </Typography>
        <HeroParagraphs paragraphs={block.paragraphs} />
      </Box>
    </LandingSection>
  );
}

/**
 * `.h1-new.is-centered` at the top of `.eth-sourc_wrap`: the title alone,
 * centred on the column's measure, 112 under the header (80 below the wide
 * step); the block under it stands 32 below, as the contain's own gap.
 */
function BareHero({ block }: { block: Hero }) {
  return (
    <LandingSection
      padding="none"
      // Under the header, which the published page lays the title's 112 (80
      // below the wide step) over: 73 tall at the wide step, 54 below it.
      sx={[NO_SECTION_TAIL, { pt: "39px", [NOT_WIDE]: { pt: "26px" } }]}
    >
      <Box sx={FLOW_MEASURE}>
        <Typography
          variant="h1"
          component="h1"
          sx={{ color: marketingPalette.ink, textAlign: "center" }}
        >
          <Lines text={block.title} />
        </Typography>
      </Box>
    </LandingSection>
  );
}

/** `.resi-number_wrap`: the hero's bullets sit on a card of their own. */
const BULLET_CARD = {
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  alignSelf: "flex-start",
  p: "16px",
  borderRadius: "12px",
  backgroundColor: marketingPalette.checksPanel,
} as const;

/** A flat ground, said as a gradient so the box keeps one shape. */
const FLAT_SURFACE = `linear-gradient(${marketingPalette.surface}, ${marketingPalette.surface})`;

/**
 * The premium grounds are stated at one angle and keep it at every width,
 * where the page's own wash and the blush both turn at the card step.
 */
function steadyWash(from: string) {
  return steady(`linear-gradient(251deg, ${from}, rgba(255, 255, 255, 0))`);
}

function steady(wash: string) {
  return { wide: wash, narrow: wash };
}

const WASH = {
  plain: SECTION_WASH,
  blush: {
    wide: "linear-gradient(251deg, rgb(255, 236, 236), rgb(255, 255, 255) 64%)",
    narrow:
      "linear-gradient(357deg, rgb(255, 236, 236), rgb(255, 255, 255) 64%)",
  },
  /** `is-prem-ded`, the two datacenter heroes. */
  mint: steadyWash(marketingPalette.sectionMint),
  /** `is-prem-st-resi`, the two static residential heroes. */
  lilac: steadyWash(marketingPalette.sectionLilac),
  // The comparison heroes are set on a flat grey.
  surface: {
    wide: FLAT_SURFACE,
    narrow: FLAT_SURFACE,
  },
  // The partner campaign boxes, each stated once and kept at every width.
  /** `is-blackdown`. */
  aqua: steady(
    "linear-gradient(251deg, rgba(75, 246, 240, 0.11), rgba(0, 56, 255, 0) 80%, rgba(0, 56, 255, 0.07))"
  ),
  /** `is-proxyway`, and `is-promo`, which repeats it. */
  blossom: steady(
    "linear-gradient(251deg, rgb(249, 252, 236), rgb(255, 241, 250) 51%, rgb(235, 255, 245))"
  ),
  /** `is-vpncenter`. */
  amber: steady(
    "linear-gradient(264deg, rgba(246, 160, 75, 0.12), rgba(0, 56, 255, 0) 48%, rgba(255, 0, 0, 0.06))"
  ),
} as const;

/** The use-case hero is set smaller than a product hero: 36 over 56 against the 52. */
const USE_CASE_TITLE = {
  fontSize: 36,
  lineHeight: "56px",
  // `.h2_new`, which the use-case pages set their `h1` in, balances its lines.
  textWrap: "balance",
  // Every step is restated: a media rule is not beaten by a plain one.
  [NOT_WIDE]: { fontSize: 36, lineHeight: "56px" },
  [MID]: { fontSize: 36, lineHeight: "48px" },
  [TINY]: { fontSize: 27, lineHeight: "35.1px" },
} as const;

/** Stacked, the box centres its two columns and closes the row's own gap. */
const STACKED = {
  flexDirection: "column",
  alignItems: "center",
} as const;

/**
 * The three boxes the published heroes draw, keyed off what the block already
 * carries. Keys run narrowest-last: same-specificity media rules, so where they
 * overlap the one written last wins.
 */
function heroPanel(block: Hero) {
  const wash = WASH[block.wash ?? "plain"];
  if (block.partner || block.promo) return partnerPanel(wash.wide);
  // The box keeps its 32 at every step, `is-smaller` or not: measured at 430
  // on the rotating and use-case heroes alike. Only `.is-youtube-proxies`,
  // the blush box, closes to 16 there.
  const tinyGap = block.wash === "blush" ? { gap: "16px" } : {};
  if (block.compactBox) {
    // The only box that carries no wash until the card step, and the one drawn
    // as a rule around white rather than a tint.
    return {
      p: `${HERO_BOX_PAD_COMPACT}px`,
      borderRadius: "32px",
      border: `1px solid ${marketingPalette.outline}`,
      gap: 0,
      [NOT_WIDE]: { ...STACKED, gap: 0 },
      [MID]: { backgroundImage: wash.narrow },
      [TINY]: { p: "12.8px", borderRadius: "24px", gap: "16px" },
    };
  }
  if (block.naturalVisual) {
    return {
      ...SECTION_PANEL,
      // The use-case box is the one drawn with no rule around it.
      border: 0,
      p: `64px 48px`,
      backgroundImage: wash.wide,
      [NOT_WIDE]: { ...STACKED, p: "32px" },
      [MID]: { borderRadius: "16px", backgroundImage: wash.narrow },
      [TINY]: { p: "8px 19.2px 19.2px", borderRadius: "24px", ...tinyGap },
    };
  }
  return {
    ...SECTION_PANEL,
    backgroundImage: wash.wide,
    // The blush turns one step earlier than the blue-grey does.
    [NOT_WIDE]: {
      ...STACKED,
      p: "32px",
      ...(block.wash === "blush" ? { backgroundImage: wash.narrow } : {}),
    },
    [MID]: { borderRadius: "16px", backgroundImage: wash.narrow },
    [TINY]: { p: "16px", ...tinyGap },
  };
}

/**
 * `.main_header_box.is-proxyway` (and `is-promo`, which repeats it): the box's
 * own 0 64 48, its copy and art 32 apart, closing to 32 all round below the
 * wide step and to 16 on a phone.
 */
function partnerPanel(wash: string) {
  return {
    ...SECTION_PANEL,
    p: "0 64px 48px",
    backgroundImage: wash,
    [NOT_WIDE]: { ...STACKED, p: "32px" },
    [MID]: { borderRadius: "16px" },
    [TINY]: { p: "16px", gap: "16px" },
  };
}

/**
 * `button_new`: what the published row sets each control to. The row itself is
 * the shrinker, a control stated at 100% gives way to the one beside it, so
 * these are widths, not flex factors, and the border each carries is what
 * splits a pair unevenly.
 */
function ctaWidths(block: Hero) {
  const multi = (block.ctas?.length ?? 0) > 1;
  if (block.ctaFit === "hug") return { "& > a, & > button": { width: "auto" } };
  const rest = { width: multi ? "100%" : CTA_LONE };
  if (block.ctaFit === "fixed") {
    return {
      "& > a, & > button": rest,
      "& > :first-of-type": { width: CTA_FIXED },
      // `.button_new.is_fixed` stops giving way below the wide step.
      [NOT_WIDE]: { "& > :first-of-type": { flex: "none" } },
    };
  }
  if (block.ctaFit === "wide") {
    return {
      "& > a, & > button": rest,
      "& > :first-of-type": { width: CTA_WIDE, minWidth: CTA_WIDE },
    };
  }
  return { "& > a, & > button": rest };
}

/**
 * The gap a stacked pair closes to: `.buttons-dc-header_list`'s 16, and its
 * 12 on a phone; every other stacked pair 12 over a line of notes and 8 bare.
 */
function stackGap(block: Hero) {
  if ((block.ctas?.length ?? 0) < 2) return "16px";
  const gap = block.reassurances?.length ? "12px" : "8px";
  return block.ctaFit === "wide" ? { at: "16px", tiny: gap } : gap;
}

/**
 * Where a row that does not hug stacks into a column of full-width controls:
 * the card step, or the tiny one for the wraps that keep their row to it.
 */
function stackedRow(block: Hero) {
  const gap = stackGap(block);
  const column = {
    flexDirection: "column",
    gap: typeof gap === "string" ? gap : gap.at,
    // Both selectors: the stated width above is the more specific of the
    // two, so the step has to clear it by name.
    "& > a, & > button": { width: "100%", minWidth: 0 },
    "& > :first-of-type": { width: "100%", minWidth: 0 },
  };
  if (block.pairRow) return { [TINY]: column };
  if (typeof gap === "string") return { [MID]: column };
  return { [MID]: column, [TINY]: { gap: gap.tiny } };
}

/** The first control is filled; the rest are outlined as the page publishes them. */
function secondVariant(index: number, block: Hero) {
  if (index === 0) return "primary";
  return block.pricingSecondary ? "pricing" : "secondary";
}

/** `btn-list`: the controls themselves, which close to 12 where they hug. */
function HeroCtaRow({ block }: { block: Hero }) {
  const ctas = block.ctas ?? [];
  const hug = block.ctaFit === "hug";
  return (
    <Box
      sx={{
        display: "flex",
        gap: hug ? "12px" : "16px",
        ...(ctas.length > 1 && !hug && !block.ctaRowFits ? { width: 1 } : {}),
        ...ctaWidths(block),
        // Only the hugging row keeps its shape stacked; every other pair
        // becomes a column of full-width controls.
        ...(hug
          ? { [MID]: { "& > a, & > button": { width: "auto" } } }
          : stackedRow(block)),
      }}
    >
      {ctas.map((cta, index) => (
        <LandingButton
          key={index}
          cta={cta}
          variant={secondVariant(index, block)}
        />
      ))}
    </Box>
  );
}

/**
 * `buttons_header-wrap`: the buttons and the line under them are one block 8
 * apart, and the block is what the column spaces. A pair fills the column and
 * centres the line; anything else closes to its own width.
 */
function ctaStack(block: Hero) {
  const column = { display: "flex", flexDirection: "column", gap: "8px" };
  if (block.ctaRowFits) {
    // `.buttons-left_wrap` is the column's item itself, as wide as its row.
    return {
      ...column,
      alignSelf: "flex-start",
      [NOT_WIDE]: { alignSelf: "center" },
      [TINY]: { alignSelf: "stretch" },
    };
  }
  if ((block.ctas?.length ?? 0) > 1 && block.ctaFit !== "hug") {
    return { ...column, width: 1, alignItems: "center" };
  }
  // A hugging row keeps its own width at every step; every other lone control
  // opens to the box at the narrowest one.
  const centred = { alignSelf: "center", alignItems: "center" };
  return {
    ...column,
    alignSelf: "flex-start",
    alignItems: "flex-start",
    [NOT_WIDE]: centred,
    [TINY]:
      block.ctaFit === "hug"
        ? centred
        : { alignSelf: "stretch", alignItems: "stretch" },
  };
}

/** `resi-number_wrap`: the checks, loose under the copy or on a ground. */
function HeroChecks({ block }: { block: Hero }) {
  const panel = block.checksPanel;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: panel ? "16px" : "10px",
        ...(panel
          ? {
              // `-extension` fits the ground to its lines; every other page
              // sets it to the measure.
              width: block.compactBox ? "auto" : 1,
              alignSelf: "flex-start",
              maxWidth: "464px",
              p: "16px",
              borderRadius: "12px",
              backgroundColor: marketingPalette.checksPanel,
              [NOT_WIDE]: { alignSelf: "center" },
              [MID]: { maxWidth: "none" },
              // On a phone the published panel insets its lines 8 from the
              // left, not 16, and sets them left in the centred column.
              [TINY]: { pl: "8px", textAlign: "left" },
            }
          : {}),
      }}
    >
      {(block.checks ?? []).map((check, index) => (
        <CheckLine
          key={index}
          label={check.label}
          icon={check.icon}
          dense={panel}
        />
      ))}
    </Box>
  );
}

/**
 * `header_left-title-content`: the proof row, the title and its copy. The
 * extension hero sets the group one step tighter and the use-case hero two,
 * so the three are named rather than shared.
 */
function groupGap(block: Hero) {
  if (block.naturalVisual) return "8px";
  return block.compactBox ? "12px" : "16px";
}

/**
 * The campaign boxes (`is-proxyway`, `is-blackdown`, `is-vpncenter`,
 * `is-promo`) set the proof row at the head of the column; every other hero
 * sets it in the title group, over the title.
 */
const proofHeadsColumn = (block: Hero) => Boolean(block.partner || block.promo);

/** The proof over the title: a review site's badge, or the customer count. */
function HeroProof({ block }: { block: Hero }) {
  return (
    <>
      {block.review ? (
        <HeroReviewRow review={block.review} fill={block.reviewFill} />
      ) : null}
      {block.socialProof ? (
        <HeroSocialProof
          label={block.socialProof}
          trustpilot={block.trustpilot}
          loose={block.looseProof}
        />
      ) : null}
    </>
  );
}

/**
 * `.h1-new.is-hero`: 20 of its own characters wide and a weight heavier, at
 * every step. Stacked, the box still stretches from the column's start to that
 * cap, and the column's centring sets its lines in the middle of the box, not
 * of the column: at 800 they sit 30 left of the column's centre.
 */
const PROMO_TITLE = {
  maxWidth: "20ch",
  fontWeight: 600,
  [TINY]: { fontWeight: 600 },
} as const;

function HeroTitleGroup({ block }: { block: Hero }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: groupGap(block),
        // `.header_left-title-content.with-padding`.
        ...(proofHeadsColumn(block) ? { mb: "16px", [MID]: { mb: 0 } } : {}),
        // `.header-left_chrome-extension`, the extension hero's (the compact
        // box's) group: its lines shrink to their words and centre below the
        // wide step, and stretch again on a phone.
        ...(block.compactBox
          ? {
              [NOT_WIDE]: { alignItems: "center" },
              [TINY]: { alignItems: "stretch" },
            }
          : {}),
      }}
    >
      {proofHeadsColumn(block) ? null : <HeroProof block={block} />}
      <Typography
        variant="h1"
        component="h1"
        sx={[
          { color: marketingPalette.ink },
          block.naturalVisual ? USE_CASE_TITLE : null,
          block.promo ? PROMO_TITLE : null,
        ]}
      >
        <Lines text={block.title} />
      </Typography>
      {block.paragraphs?.map((paragraph, index) => (
        <Typography
          key={index}
          sx={[
            index === 0 && !block.naturalVisual ? BODY : BODY_SMALL,
            // `body_new.is-medium`: the lead line the one hero sets in the
            // heavier weight, over the quieter line under it.
            index === 0 && block.leadEmphasis ? { fontWeight: 500 } : null,
          ]}
        >
          {paragraph}
        </Typography>
      ))}
    </Box>
  );
}

/** `main_header_left.is-proxyway`: half the box, 16 apart, 8 on a phone. */
const PARTNER_COLUMN = {
  flex: "0 0 auto",
  width: "50%",
  maxWidth: `${HERO_COPY_WIDTH}px`,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "16px",
  [NOT_WIDE]: {
    width: 1,
    maxWidth: "100%",
    alignItems: "center",
    textAlign: "center",
  },
  [TINY]: { gap: "8px" },
} as const;

/** `main_header_left`: the measure the copy is set to, at each step. */
function copyColumn(block: Hero) {
  if (block.partner) return PARTNER_COLUMN;
  const measure = block.narrowColumn ? HERO_COPY_NARROW : HERO_COPY_WIDTH;
  return {
    flex: "0 0 auto",
    width: 1,
    maxWidth: block.image ? `${measure}px` : "none",
    display: "flex",
    flexDirection: "column",
    gap: "32px",
    // One key, not two: a second computed `[NOT_WIDE]` in the same
    // literal silently replaces the first.
    //
    // `.main_header_left` below the wide step: the column runs the box's
    // width and centres each part on its own, so a part that spans the
    // column (a filling pair of controls) takes the box's width, not the
    // widest heading's.
    [NOT_WIDE]: {
      gap: "24px",
      textAlign: "center",
      width: 1,
      alignItems: "center",
      // `min()`, not the bare measure: a rating wider than the column
      // pushes the whole column off the page.
      maxWidth: block.narrowColumn
        ? `min(${HERO_COPY_NARROW}px, 100%)`
        : "100%",
    },
    // `.main_header_left.is-extension-header` on a phone: 16 under the column.
    [TINY]: { gap: "20.8px", ...(block.naturalVisual ? { mb: "16px" } : {}) },
  };
}

/** `label_wrap`: the one hero that opens on a chip rather than a rating. */
function HeroEyebrow({ label }: { label: string }) {
  return (
    <Box
      sx={{
        alignSelf: "flex-start",
        px: "10px",
        py: "4px",
        borderRadius: "6px",
        backgroundColor: marketingPalette.labelChip,
        fontSize: 13,
        textTransform: "uppercase",
      }}
    >
      {label}
    </Box>
  );
}

/** `resi-number_wrap`: the lines the two published heroes set on a card. */
function HeroBullets({ items }: { items: ReadonlyArray<string> }) {
  return (
    <Box sx={BULLET_CARD}>
      {items.map((bullet, index) => (
        <CheckLine key={index} label={bullet} />
      ))}
    </Box>
  );
}

/** `buttons_header-wrap`: the controls and the line under them. */
function HeroCtaGroup({ block }: { block: Hero }) {
  const ctas = block.ctas ?? [];
  const reassurances = block.reassurances ?? [];
  if (!ctas.length && !reassurances.length) return null;
  return (
    <Box sx={ctaStack(block)}>
      {ctas.length ? <HeroCtaRow block={block} /> : null}
      {reassurances.length ? (
        <ReassuranceStrip items={reassurances} shrink={block.chipsShrink} />
      ) : null}
    </Box>
  );
}

/** `header_left_top_content`: the crumb over the rating, 12 apart, on the column's width. */
const TOP_CONTENT = {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  width: 1,
} satisfies SxProps<Theme>;

/**
 * `header_left_top_content`: the crumb and the rating, 12 apart. One rating
 * widget per hero: where there is a social-proof row, that row holds it.
 */
function HeroTop({ block }: { block: Hero }) {
  const rating = block.trustpilot && !block.socialProof;
  if (!block.breadcrumb && !rating) {
    return null;
  }
  return (
    <Box sx={TOP_CONTENT}>
      {block.breadcrumb ? (
        <Breadcrumb
          trail={block.breadcrumb}
          useCase={block.useCaseTrail === true}
        />
      ) : null}
      {rating ? <HeroTrustpilot /> : null}
    </Box>
  );
}

/** `main_header_left`: everything the hero says, in one column. */
function HeroCopy({ block }: { block: Hero }) {
  const bullets = block.bullets ?? [];
  const checks = block.checks ?? [];
  return (
    <Box sx={copyColumn(block)}>
      {proofHeadsColumn(block) ? <HeroProof block={block} /> : null}
      <HeroTop block={block} />
      {block.eyebrow ? <HeroEyebrow label={block.eyebrow} /> : null}
      <HeroTitleGroup block={block} />
      {bullets.length ? <HeroBullets items={bullets} /> : null}
      {checks.length ? <HeroChecks block={block} /> : null}
      {/* The empty holder the published column carries, for its gap. */}
      {block.ghostControl ? <Box aria-hidden /> : null}
      <HeroCtaGroup block={block} />
    </Box>
  );
}

/**
 * `main_header_right`: the art, drawn in a square that shrinks with the column.
 * The published box states its height as a percentage of its own width, so the
 * hero closes up at 1000 rather than holding the desktop height.
 */
function HeroVisual({ block }: { block: Hero }) {
  const natural = block.naturalVisual;
  return (
    <Box
      sx={{
        position: "relative",
        flex: "0 0 auto",
        width: `${HERO_VISUAL_WIDTH}px`,
        // A basis rather than a fixed size: between the wide step and the
        // frame's own cap the column has less than 600 to give, and the
        // published art shrinks there rather than pushing the page wide.
        maxWidth: natural ? `min(${HERO_ART_CAP}px, 100%)` : "100%",
        minWidth: 0,
        flexShrink: 1,
        ...(natural
          ? {}
          : { aspectRatio: "1", borderRadius: "16px", overflow: "hidden" }),
        // `with-padding`: the extension's art is set 40 in from where the row
        // would place it and runs 40 past the box's own padding, which is
        // where the published page draws it. The two cancel, so the art keeps
        // the width it would have had.
        ...(block.compactBox
          ? { ml: "40px", mr: "-40px", [NOT_WIDE]: { ml: 0, mr: 0 } }
          : {}),
      }}
    >
      {natural ? (
        // `.use-case-main-header_height`: a box 61% as tall as it is wide,
        // the file fitted inside it.
        <Box sx={{ position: "relative", width: 1, pt: "61%" }}>
          <LandingVisual
            src={block.image ?? ""}
            eager
            sx={{
              position: "absolute",
              inset: 0,
              width: 1,
              height: 1,
              objectFit: "contain",
              borderRadius: "16px",
            }}
          />
        </Box>
      ) : (
        <LandingVisual
          src={block.image ?? ""}
          eager
          sx={{ width: 1, height: 1, objectFit: "contain" }}
        />
      )}
      {/* `use_case-card_label`: a pill on the artwork's top right. */}
      {block.badge ? (
        <Box
          sx={{
            position: "absolute",
            top: "8px",
            right: "8px",
            zIndex: 1,
            px: "10.08px",
            py: "2.08px",
            borderRadius: "999px",
            backgroundColor: "rgba(255, 255, 255, 0.22)",
            color: "common.white",
            fontSize: 13,
            lineHeight: "19.5px",
            fontWeight: 300,
          }}
        >
          {block.badge}
        </Box>
      ) : null}
    </Box>
  );
}

/** The box's other half: the partner's badge, or the art every other hero draws. */
function HeroArt({ block }: { block: Hero }) {
  if (!block.image) {
    return null;
  }
  return block.partner ? (
    <PartnerBadge src={block.image} />
  ) : (
    <HeroVisual block={block} />
  );
}

/**
 * `.proxyway-badge_img`: the partner's award badge, a square filling the half
 * of the box the copy leaves, the full width once the box stacks.
 */
function PartnerBadge({ src }: { src: string }) {
  return (
    <LandingVisual
      src={src}
      eager
      sx={{
        flex: "0 1 auto",
        minWidth: 0,
        width: "50%",
        maxWidth: "none",
        aspectRatio: "1",
        objectFit: "cover",
        [NOT_WIDE]: { width: 1 },
      }}
    />
  );
}

/** The section's own rhythm, where a page sets its hero inside the banner spacer. */
function heroSection(block: Hero): SxProps<Theme> {
  if (block.afterTabs) {
    return [
      // Restated at every step, as below: the padding named here is the
      // switcher's own margin under it, not the hero's box padding.
      {
        pt: "48px",
        [NOT_WIDE]: { pt: "48px" },
        [MID]: { pt: "16px" },
        [TINY]: { pt: "16px" },
      },
      NO_SECTION_TAIL,
    ];
  }
  if (!block.bannerSpacer) return NO_SECTION_TAIL;
  return [
    // Restated at every step: the section's own narrow padding is a
    // media rule and would otherwise win below the wide step.
    {
      pt: `${40 + HERO_BANNER_SPACER}px`,
      [NOT_WIDE]: { pt: `${10 + HERO_BANNER_SPACER}px` },
      [TINY]: { pt: `${10 + HERO_BANNER_SPACER}px` },
    },
    NO_SECTION_TAIL,
  ];
}

export default function HeroBlock({
  block: content,
}: {
  block: Extract<LandingBlock, { kind: "hero" }>;
}) {
  const block: Hero = { ...content, ...heroLayout(usePageIdentity()) };
  if (block.bare) return <BareHero block={block} />;
  if (block.align === "center") return <CentredHero block={block} />;
  if (block.sectionHeading) return <HeadingHero block={block} />;
  return (
    <LandingSection padding="hero" sx={heroSection(block)}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "32px",
          ...heroPanel(block),
        }}
      >
        <HeroCopy block={block} />
        <HeroArt block={block} />
      </Box>
      {block.table ? (
        <Box sx={{ mt: "48px", [TINY]: { mt: "32px" } }}>
          <MatrixTable
            columns={block.table.columns}
            rows={block.table.rows}
            verdict
          />
        </Box>
      ) : null}
    </LandingSection>
  );
}
